import React, { useMemo, useState, useEffect, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { FilterMatchMode } from "primereact/api";
import "./historial-vehiculo-table.css";
import useMedia from '../../hooks/media-querys'

/**
 * Espera un prop `data` con objetos de la forma:
 * {
 *   numeroOT, mecanico_id, mecanico_nombre, mecanico2_nombre, servicio_tipo,
 *   fechaIngreso, fechaSalida, marca, modelo, anio, nmotor, nserie,
 *   kilometrajeAct, kilometrajeProx, combustible, sintomas, observaciones,
 *   recomendacion, diagnostico, obsMecanico, repuestos, ultimaReparacion,
 *   evaluaciones, cliente_nombre, cliente_rut, cliente_correo, cliente_celular
 * }
 */
function HistorialVehiculoTable({ data = [], dtRef, onVisibleChange  }) {
  const isPhone  = useMedia('(max-width: 640px)');

  const [sortState, setSortState] = useState({
    sortField: null,
    sortOrder: null,        // 1 asc, -1 desc
    multiSortMeta: null,    // para sort múltiple
  })

  const handleSort = (e) => {
    const { sortField, sortOrder, multiSortMeta } = e;
    setSortState({ sortField, sortOrder, multiSortMeta: multiSortMeta ?? null });
  };

  // Normalizar data para filtros de arreglos
  const dataNorm = useMemo(() => {
    const norm = (s) =>
      (s ?? "").toString().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

    const repuestoToText = (r) => 
      `${r?.cantidad ?? ""} ${r?.unidad ?? ""} ${r?.nombre ?? ""}`.trim();
    
    const evaluacionToText = (e) => 
      `${e?.nombre ?? ""} ${e?.estado ?? ""}`.trim();
    
    return (data || []).map((row) => {
      const repuestosText = Array.isArray(row.repuestos)
        ? row.repuestos.map(repuestoToText).join(" | ")
        : "";

      const evaluacionesText = Array.isArray(row.evaluaciones)
        ? row.evaluaciones.map(evaluacionToText).join(" | ")
        : "";

      return {
        ...row,
        repuestosText: norm(repuestosText),
        evaluacionesText: norm(evaluacionesText),
        sintomas_norm: norm(row.sintomas),
        diagnostico_norm: norm(row.diagnostico),
        recomendacion_norm: norm(row.recomendacion),
        servicio_tipo_norm: norm(row.servicio_tipo),
      };
    });
  }, [data]);

  // --- helpers de exportación de celdas ---
  const repuestosExport = (row) =>
    Array.isArray(row.repuestos) && row.repuestos.length
      ? row.repuestos
          .map((r) => `${r?.cantidad ?? ""} ${r?.unidad ?? ""} ${r?.nombre ?? ""}`.trim())
          .filter(Boolean)
          .join(" | ")
      : "";

  const evaluacionesExport = (row) =>
    Array.isArray(row.evaluaciones) && row.evaluaciones.length
      ? row.evaluaciones
          .map((e) => `${e?.nombre ?? ""}: ${e?.estado ?? ""}`.trim())
          .filter(Boolean)
          .join(" | ")
      : "";

  // Columnas definidas en un array
  const columns = useMemo(
    () => [
      { 
        field: "servicio_tipo", header: "Servicio", 
        filterField: "servicio_tipo_norm",
        sortField: "servicio_tipo_norm",
        body: (d) => <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.servicio_tipo}</div>,
        exportBody: (r) => r.servicio_tipo ?? "", 
        style: { minWidth: "12rem" } 
      },
      { 
        field: "sintomas", header: "Síntomas", 
        filterField: "sintomas_norm",
        sortField: "sintomas_norm",
        body: (d) => <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.sintomas}</div>,
        exportBody: (r) => r.sintomas ?? "",
        style: { minWidth: "18rem", maxWidth: "24rem" } 
      },
      { 
        field: "evaluaciones", header: "Evaluaciones", 
        filterField: "evaluacionesText",
        sortField: "evaluacionesText",
        body: evaluacionesBody, style: { minWidth: "18rem" },
        exportBody: evaluacionesExport,
      },
      { 
        field: "diagnostico", header: "Diagnóstico", 
        filterField: "diagnostico_norm",
        sortField: "diagnostico_norm",
        body: d => <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.diagnostico}</div>, 
        exportBody: (r) => r.diagnostico ?? "",
        style: { minWidth: "18rem", maxWidth: "24rem" }
      },
      { 
        field: "recomendacion", header: "Trabajo Realizado", 
        filterField: "recomendacion_norm",
        sortField: "recomendacion_norm",
        body: d => <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.recomendacion}</div>, 
        exportBody: (r) => r.recomendacion ?? "",
        style: { minWidth: "18rem", maxWidth: "24rem" }
      },
      { 
        field: "repuestos", header: "Repuestos", 
        filterField: "repuestosText",
        sortField: "repuestosText",
        body: repuestosBody, 
        exportBody: repuestosExport,
        style: { minWidth: "16rem" } 
      },
    ], []
  );

  const searchableFields = useMemo(
    () => columns.map((c) => c.filterField ?? c.field),
    [columns]
  );

  // Filtros (global + por columna) generados dinámicamente
  const initialFilters = useMemo(() => {
    const perColumn = columns.reduce((acc, c) => {
      const key = c.filterField ?? c.field;
      acc[key] = { value: null, matchMode: FilterMatchMode.CONTAINS };
      return acc;
    }, {});
    return {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      ...perColumn,
    };
  }, [columns]);

  const [filters, setFilters] = useState(initialFilters);
  const [globalValue, setGlobalValue] = useState("");

  const onGlobalFilterChange = (e) => {
    const raw = e.target.value || "";
    setGlobalValue(raw);

    const normalized = raw.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
    setFilters((prev) => ({ ...prev, global: { ...prev.global, value: normalized } }));
  };

  const handleColumnFilter = (e) => {
    const norm = (s) => (s ?? "").toString().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
    const next = { ...e.filters };
    Object.keys(next).forEach((k) => {
      if (typeof next[k]?.value === "string") next[k] = { ...next[k], value: norm(next[k].value) };
    });
    setFilters(next);
  };

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    const na = Number(a), nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.toString().localeCompare(b.toString(), undefined, { numeric: true, sensitivity: "base" });
  };

  const computeFilteredRows = useCallback(() => {
    const hasText = (v) => typeof v === "string" && v.length > 0;

    const global = filters?.global?.value || "";
    const hasGlobal = hasText(global);

    let out = (dataNorm || []).filter((row) => {
      if (hasGlobal) {
        const okGlobal = searchableFields.some((f) => (row?.[f] ?? "").toString().includes(global));
        if (!okGlobal) return false;
      }
      for (const [k, cfg] of Object.entries(filters || {})) {
        if (k === "global") continue;
        const val = cfg?.value ?? null;
        if (hasText(val)) {
          const cell = (row?.[k] ?? "").toString();
          if (!cell.includes(val)) return false;
        }
      }
      return true;
    });

    // Ordenar
    const { sortField, sortOrder, multiSortMeta } = sortState;

    if (multiSortMeta?.length) {
      out = [...out].sort((ra, rb) => {
        for (const meta of multiSortMeta) {
          const a = ra?.[meta.field], b = rb?.[meta.field];
          const res = cmp(a, b);
          if (res !== 0) return meta.order === 1 ? res : -res;
        }
        return 0;
      });
    } else if (sortField && sortOrder) {
      out = [...out].sort((ra, rb) => {
        const res = cmp(ra?.[sortField], rb?.[sortField]);
        return sortOrder === 1 ? res : -res;
      });
    }

    return out;
  }, [dataNorm, filters, searchableFields, sortState]);

  useEffect(() => {
    onVisibleChange?.(computeFilteredRows());
  }, [computeFilteredRows, onVisibleChange]);

  // Header de la tabla
  const renderHeader = () => {
    const HeadingTag = isPhone ? "h5" : "h4";
    return (
      <div className="flex flex-wrap gap-2 justify-content-between align-items-center">
        <HeadingTag className="m-0">Historial del Vehiculo</HeadingTag>
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search pr-2" />
          <InputText 
            value={globalValue} 
            onChange={onGlobalFilterChange} 
            placeholder='Buscar en la tabla'
            className='pl-6 input-text-m'
          />
        </IconField>
      </div>
    )
  };

  return (
    <div className="card">
      <div className="hist-table inv-data-table">
        <DataTable 
          ref={dtRef} 
          value={dataNorm}
          dataKey="numeroOT"
          header={renderHeader}
          paginator rows={10} rowsPerPageOptions={[10, 20, 50]}
          stripedRows removableSort 
          resizableColumns columnResizeMode="expand"
          scrollable scrollHeight="flex" scrollDirection="both"
          filters={filters} 
          onFilter={handleColumnFilter} 
          filterDisplay="row" 
          globalFilterFields={searchableFields}
          sortMode="single"
          sortField={sortState.sortField}
          sortOrder={sortState.sortOrder}
          onSort={handleSort}
          emptyMessage="Sin resultados."
          size='medium' 
        >
          {columns.map((col) => (
            <Column 
              key={col.field}
              field={col.field} 
              header={col.header}
              style={col.style} 
              body={col.body}
              sortable
              filter
              filterField={col.filterField}
              sortField={col.sortField}
              filterPlaceholder={`Filtrar ${col.header.toLowerCase()}`}
              filterMatchMode={FilterMatchMode.CONTAINS}
              exportBody={col.exportBody}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
}

export default React.memo(HistorialVehiculoTable);

/* ===== Helpers de render ===== */
// Render para evaluaciones
const evaluacionesBody = (rowData) => {
  if (!rowData.evaluaciones || rowData.evaluaciones.length === 0) return "-";
  return (
    <ul className="m-0 pl-3">
      {rowData.evaluaciones.map((e) => (
        <li key={e.id}>
          {e.nombre}, {e.estado}
        </li>
      ))}
    </ul>
  );
};

// Render para repuestos
const repuestosBody = (rowData) => {
  if (!rowData.repuestos || rowData.repuestos.length === 0) return "-";
  return (
    <ul className="m-0 pl-3">
      {rowData.repuestos.map((r, i) => (
        <li key={i}>
          {r.cantidad} {r.unidad} {r.nombre}
        </li>
      ))}
    </ul>
  );
};

